import Utilities from "../../foundation/Utility";
import React from "react";
import { createPortal } from "react-dom";
import ReactTooltip from "react-tooltip";

import "../../components/dialog/Dialog.scss";

interface DialogProps {
    children?: any;
    dialogIcon?: string;
    dialogIconStyle?: string;
    dialogTitle?: string;
    width?: number;
    zIndex?: number;
    initOpen?: boolean;
    showDialog?: boolean;
    showLoading?: boolean;
    canResize?: boolean;
    canDrag?: boolean;
    disableEscape?: boolean;
    stage?: number;
    maskClick?: Function;
}

interface DialogState {
    rendered: boolean;
    maximised: boolean;
    dragging: boolean;
    opening: boolean;
    open: boolean;
    closing: boolean;
    top?: number;
    left?: number;
    dragged: boolean;
    container: React.RefObject<HTMLElement>;
    dialog: React.RefObject<HTMLDivElement>;
    focusTrapClear: () => void;
}

class Dialog extends React.Component<DialogProps, DialogState> {
    state = {
        rendered: false,
        maximised: false,
        dragging: false,
        opening: false,
        open: false,
        closing: false,
        dragged: false,
        container: React.createRef<HTMLElement>(),
        dialog: React.createRef<HTMLElement>(),
        focusTrapClear: null,
    } as DialogState;

    componentDidUpdate(
        prevProps: Readonly<DialogProps>,
        prevState: Readonly<DialogState>,
        snapshot?: any,
    ): void {
        if (this.props.stage && this.props.stage !== prevProps.stage) {
            if (this.state.focusTrapClear) this.state.focusTrapClear();
            setTimeout(() => {
                const trapClear = Utilities.createFocusTrap(
                    this.state.container.current,
                );
                this.setState(() => ({
                    open: true,
                    opening: false,
                    focusTrapClear: trapClear,
                }));
                ReactTooltip.rebuild();
            }, 500);
        }

        if (!prevProps.showDialog && this.props.showDialog) {
            this.setState(() => ({ rendered: true }));
            setTimeout(() => {
                this.setState(() => ({ opening: true }));
                setTimeout(() => {
                    const trapClear = Utilities.createFocusTrap(
                        this.state.container.current,
                    );
                    this.setState(() => ({
                        open: true,
                        opening: false,
                        focusTrapClear: trapClear,
                    }));
                    ReactTooltip.rebuild();
                }, 500);
            }, 10);
        } else if (prevProps.showDialog && !this.props.showDialog) {
            if (this.state.focusTrapClear) this.state.focusTrapClear();
            this.setState(() => ({
                rendered: false,
                open: false,
                closing: false,
                focusTrapClear: null,
            }));
        }
    }

    setMaximised(value: boolean) {
        this.setState(() => ({
            maximised: value,
        }));
    }
    maskClick(event) {}
    maxClick(event) {
        this.setMaximised(!this.state.maximised);
    }
    dragDown(event) {
        if (!this.state.maximised) {
            const offset = {
                top: event.clientY - this.state.dialog.current.offsetTop,
                left: event.clientX - this.state.dialog.current.offsetLeft,
            };

            this.setState({ dragging: true });

            const onMouseMove = (e: MouseEvent) => {
                this.setState({
                    top: Math.min(
                        window.innerHeight -
                            this.state.dialog.current.offsetHeight,
                        Math.max(0, e.clientY - offset.top),
                    ),
                    left: Math.min(
                        window.innerWidth -
                            this.state.dialog.current.offsetWidth,
                        Math.max(0, e.clientX - offset.left),
                    ),
                    dragged: true,
                });
            };

            const onMouseUp = () => {
                window.removeEventListener("mousemove", onMouseMove);
                window.removeEventListener("mouseup", onMouseUp);
                this.setState({ dragging: false });
            };

            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
        }
    }

    render() {
        return (
            this.state.rendered &&
            createPortal(
                <section
                    ref={this.state.container}
                    className={`dialog-section${
                        (this.state.opening || this.state.open) &&
                        !this.state.closing
                            ? " visible"
                            : ""
                    }`}
                    style={{ zIndex: this.props.zIndex ?? 999 }}>
                    <div
                        className="mask"
                        style={{ zIndex: (this.props.zIndex ?? 999) - 1 }}></div>
                    <div
                        className={`dialog-outer${
                            this.state.opening || this.state.open ? " open" : ""
                        }${this.state.dragging ? " dragging" : ""}${
                            this.state.maximised ? " maximised" : ""
                        }`}
                        style={{ zIndex: this.props.zIndex ?? 999 }}>
                        <div
                            className="dialog-scroll"
                            onClick={this.maskClick}>
                            <div
                                ref={this.state.dialog}
                                className={`dialog-container${
                                    this.state.maximised ? " maximised" : ""
                                }${this.state.dragged ? " dragged" : ""}`}
                                style={{
                                    width: (this.props.width ?? 600) + "px",
                                    top: this.state.top
                                        ? this.state.top + "px"
                                        : null,
                                    left: this.state.left
                                        ? this.state.left + "px"
                                        : null,
                                }}>
                                <div className="dialog-inner">
                                    <header
                                        className={`dialog-title ${
                                            this.props.dialogIconStyle ?? ""
                                        }`}
                                        onMouseDown={this.dragDown.bind(this)}>
                                        {this.props.dialogIcon && (
                                            <i className="dialog-title-icon">
                                                {this.props.dialogIcon}
                                            </i>
                                        )}
                                        <span className="dialog-title-text">
                                            {this.props.dialogTitle}
                                        </span>
                                    </header>
                                    <div className="dialog-content">
                                        {(this.state.open ||
                                            this.state.opening) &&
                                            this.props.children}
                                    </div>
                                    {this.props.showLoading && (
                                        <div className="dialog-mask">
                                            <div className="loader"></div>
                                        </div>
                                    )}
                                </div>
                                <div className="dialog-controls">
                                    {this.props.canResize !== false && (
                                        <i
                                            className="dialog-maximise fa-solid grey fa-maximise"
                                            data-tip="Maximise or restore window"
                                            onClick={this.maxClick.bind(this)}>
                                            crop_square
                                        </i>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>,
                document.getElementsByClassName("page-elements")[0],
            )
        );
    }
}

export default Dialog;
